import { useState, useEffect } from 'react';
import api from '../utils/api';

export default function PayeeReviewPanel({ items, onClose, onDone }) {
  const [vendors, setVendors] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [rows, setRows] = useState(items.map(i => ({ ...i, _selection: null, _search: '', _committed: false })));
  const [committing, setCommitting] = useState(false);
  const [commitSummary, setCommitSummary] = useState(null);

  useEffect(() => {
    api.get('/vendors').then(r => setVendors(r.data));
    api.get('/contractors').then(r => setContractors(r.data));
  }, []);

  function updateRow(idx, patch) {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }

  function selectExisting(idx, id, label, type) {
    updateRow(idx, { _selection: { type, id, label } });
  }

  function selectCreateNew(idx, type) {
    updateRow(idx, { _selection: { type, create_new: true, label: `Create new ${type}` } });
  }

  function clearSelection(idx) {
    updateRow(idx, { _selection: null });
  }

  const selectedCount = rows.filter(r => r._selection && !r._committed).length;

  async function commitAll() {
    setCommitting(true);
    let succeeded = 0, failed = 0;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row._selection || row._committed) continue;
      try {
        await api.post('/payee-import/resolve', {
          payee_name: row.payee_name, first_name: row.first_name, last_name: row.last_name,
          routing_number: row.routing_number, account_number: row.account_number, bank_name: row.bank_name,
          address: row.address, phone: row.phone,
          target: row._selection.create_new
            ? { type: row._selection.type, create_new: true }
            : { type: row._selection.type, id: row._selection.id },
        });
        updateRow(i, { _committed: true });
        succeeded++;
      } catch (e) {
        failed++;
      }
    }
    setCommitting(false);
    setCommitSummary({ succeeded, failed });
  }

  const remaining = rows.filter(r => !r._committed).length;

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 640 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">Review Unmatched Payees ({remaining} remaining)</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="panel-body">
          {commitSummary && (
            <div style={{ background: 'var(--info-bg)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              Committed {commitSummary.succeeded} selections{commitSummary.failed > 0 ? `, ${commitSummary.failed} failed` : ''}.
            </div>
          )}
          {rows.map((row, idx) => {
            if (row._committed) {
              return (
                <div key={idx} style={{ padding: 10, background: 'var(--success-bg, var(--surface))', borderRadius: 'var(--radius)', marginBottom: 8, fontSize: 13 }}>
                  ✓ {row.payee_name} → {row._selection.label} — committed
                </div>
              );
            }
            const pool = row.payee_type === 'Individual' ? contractors : vendors;
            const poolLabel = row.payee_type === 'Individual' ? 'contractor' : 'vendor';
            const nameKey = row.payee_type === 'Individual' ? null : 'name';
            const filtered = row._search
              ? pool.filter(p => {
                  const label = nameKey ? p[nameKey] : `${p.first_name} ${p.last_name}`;
                  return label.toLowerCase().includes(row._search.toLowerCase());
                }).slice(0, 8)
              : [];
            const isSelected = !!row._selection;

            return (
              <div
                key={idx}
                style={{
                  padding: 12, borderRadius: 'var(--radius)', marginBottom: 10,
                  background: isSelected ? 'var(--info-bg, var(--surface))' : 'var(--surface)',
                  border: isSelected ? '1px solid var(--primary)' : undefined,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{row.payee_name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                  {row.payee_type} · Routing: {row.routing_number || '—'} · Account: {row.account_number || '—'}
                  {row.best_guess_name && ` · Best guess: "${row.best_guess_name}" (${row.best_guess_score}%)`}
                  {row.already_has_bank_info && <span style={{ color: 'var(--danger-text)' }}> · ⚠ this match already has bank details on file</span>}
                </div>

                {isSelected ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Selected: {row._selection.label}</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => clearSelection(idx)}>Change</button>
                  </div>
                ) : (
                  <>
                    {row.best_guess_id && row.best_guess_score >= 75 && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginBottom: 8, marginRight: 8 }}
                        onClick={() => selectExisting(idx, row.best_guess_id, row.best_guess_name, poolLabel)}
                      >
                        Use best guess: "{row.best_guess_name}"
                      </button>
                    )}

                    <div className="form-field" style={{ marginBottom: 8 }}>
                      <label>Search existing {poolLabel}s</label>
                      <input
                        type="text"
                        value={row._search}
                        onChange={e => updateRow(idx, { _search: e.target.value })}
                        placeholder={`Type to search ${poolLabel}s…`}
                      />
                    </div>
                    {filtered.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {filtered.map(p => {
                          const label = nameKey ? p[nameKey] : `${p.first_name} ${p.last_name}`;
                          return (
                            <button
                              key={p.id}
                              className="btn btn-ghost btn-sm"
                              style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }}
                              onClick={() => selectExisting(idx, p.id, label, poolLabel)}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => selectCreateNew(idx, poolLabel)}
                    >
                      + Create New {poolLabel === 'vendor' ? 'Vendor' : 'Contractor'} from this payee
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button
            className="btn btn-primary"
            onClick={remaining === 0 ? onDone : commitAll}
            disabled={committing || (remaining > 0 && selectedCount === 0)}
          >
            {remaining === 0 ? 'Done' : committing ? 'Committing…' : `Commit All Selected (${selectedCount})`}
          </button>
        </div>
      </div>
    </>
  );
}
